import { Injectable } from '@angular/core';
import { EnumList } from '../interfaces/i-enum';
import { TypeValue } from '../interfaces/i-type';
import { ActivatedRouteSnapshot } from '@angular/router';
import { OverlayService } from '@clavisco/overlay';
import { finalize, tap, catchError } from 'rxjs';
import { ReportService } from './report.service';
import { IPermission } from '../interfaces/i-permission';
import { format } from 'date-fns';
import { formatDate } from '@angular/common';
export enum DateFormatType{
  Corta = 'corta',
  Larga = 'larga'
}
@Injectable({
  providedIn: 'root'
})
export class GlobalService {

  constructor(
    private clBlockUI: OverlayService,
    private reportService: ReportService
  ) { }


  /**
 * Converts an enum into a list of objects with translated names.
 *
 * @param enumType - The enum to convert.
 * @param translations - An optional mapping of enum keys to translated names.
 * @returns A list of objects containing the enum values and their names.
 */
  ConvertEnumIntoList<T extends Record<string, string | number>>(
    enumType: T,
    translations?: Record<string, string>
  ): EnumList[] {
    let map: EnumList[] = [];

    for (const key in enumType) {
      if (!isNaN(Number(key))) continue; // Ignore numeric keys (reverse mapping of enums)

      map.push({
        id: enumType[key] as number,
        name: translations?.[key] || key // Use translation if available, otherwise use enum key
      });
    }

    return map;
  }

  /**
   * Gets a list of default type values.
   *
   * @returns A list of TypeValue objects.
   */
  GetTypeValues(): TypeValue[] {
    let typeValueList: TypeValue[] = [
      {
        id: '00',
        name: 'Inducolcarnes'
      }
    ];
    return typeValueList;
  }

  /**
   * Gets a list of default payment type values.
   *
   * @returns A list of TypeValue objects.
   */
  GetPaymentTypeValues(): TypeValue[] {
    let typeValueList: TypeValue[] = [
      {
        id: '01',
        name: 'Realizar Pago'
      },
      {
        id: '02',
        name: 'Replanillar'
      },
      {
        id: '03',
        name: 'Devolución'
      },
      {
        id: '04',
        name: 'No Transaccionar'
      }
    ];
    return typeValueList;
  }

  /**
   * Gets the required permission for a given route.
   *
   * @param route - The route for which the permission is required.
   * @returns The name of the permission required for that route.
   */
  GetPermissionsByPath(route: string): string {
    let reqPerm: string = '';
    switch (route) {
      case 'documents':
        reqPerm = 'M_Documents';
        break;
      case 'settings':
        reqPerm = 'M_Settings';
        break;
      case 'collectionView':
        reqPerm = 'S_CollectionView';
        break;
      case 'spreadsheetCreation':
        reqPerm = 'S_SpreadCreation';
        break;
      case 'spreadsheetDetail':
        reqPerm = 'F_SpreadDetail';
        break;
      case 'users':
        reqPerm = 'S_Users';
        break;
      case 'rolePerm':
        reqPerm = 'S_RolsPerm';
        break;
      default:
        break;
    }
    return reqPerm;
  }

  /**
 * Validates if a specific permission is present in a list of permissions.
 *
 * @param perms - List of permissions to validate against.
 * @param reqPerm - The required permission to validate.
 * @returns True if the required permission is present in the list, otherwise false.
 */
  ValidatePermission(perms: IPermission[], reqPerm: string): boolean {
    return perms.some((p: IPermission) => p.Name === reqPerm);
  }

  /**
   * Gets the full path of an active route, including child routes.
   *
   * @param route - The ActivatedRouteSnapshot object containing the current route.
   * @returns The full route path as a string.
   */
  getRoutePath(route: ActivatedRouteSnapshot): string {
    let routePath = '';
    if (route.routeConfig) {
      routePath += route.routeConfig.path;
    }
    if (route.firstChild) {
      routePath += this.getRoutePath(route.firstChild);
    }
    return routePath;
  }

  /**
   * Previews a report by fetching the spreadsheet document and opening it in a new tab.
   *
   * @param docId - The ID of the document to preview.
   */
  PreviewReport(docId: number) {
    this.clBlockUI.OnGet("Previsualizando reporte de planilla")
    this.reportService.GetReportSpredsheet(docId)
      .pipe(finalize(() => {
        this.clBlockUI.Drop()
      }), tap((data) => {
        var fileBase64 = atob(data.Data);
        var length = fileBase64.length;
        var arrayBuffer = new ArrayBuffer(length);
        var uintArray = new Uint8Array(arrayBuffer);
        for (var i = 0; i < length; i++) {
          uintArray[i] = fileBase64.charCodeAt(i);
        }
        var tab = window.open();
        var pdfFile = new Blob([uintArray], { type: 'application/pdf' });
        var fileUrl = URL.createObjectURL(pdfFile);
        if (tab !== null) {
          tab.location.href = fileUrl;
        }
      }),
        catchError((err) => {
          return [];
        })
      ).subscribe();
  }

  /**
   * Formats a number as currency in the format "$X.XX".
   *
   * @param amount - The amount to format.
   * @returns The formatted currency string.
   */
  formatAmount(amount: number): string {
    const formatted = new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);

    return `$${formatted}`;
  }

  formatDate(date: Date): string {
    const newDate: Date = new Date(date);
    const year = newDate.getFullYear();
    const month = (newDate.getMonth()+1).toString().padStart(2,'0');
    const day = (newDate.getDate()).toString().padStart(2,'0');
    const hour = (newDate.getHours()).toString().padStart(2,'0');
    const minute = (newDate.getMinutes()).toString().padStart(2,'0');
    const second = (newDate.getSeconds()).toString().padStart(2,'0');


    return `${month}/${day}/${year} ${hour}:${minute}:${second}`;
  }

  /**
   * Formats a date as a string in the extended ISO 8601 format with time zone "yyyy-MM-dd'T'HH:mm:ssXXX".
   *
   * @param date - The date to format.
   * @returns The formatted date string.
   */
  formatUtcDateTime(date: Date){
    return  format(date, "yyyy-MM-dd'T'HH:mm:ssXXX");
  }

  /**
   * Convert incoming dates to local time using the ISO-8601 format. This method returns the date in two formats: short (yyyy-MM-dd) and long (yyyy-MM-ddTHH:mm:ss)
   *
   * @param _date - The date to format.
   * @param _type - The type to date (corta (yyyy-MM-dd), larga (yyyy-MM-ddTHH:mm:ss)).
   * @returns The formatted date string.
   */
  formatDateToISO8601(_date: Date = new Date(), _type: DateFormatType = DateFormatType.Larga): string {
    const now = new Date();
    if(_date.toDateString() === now.toDateString()){
      _date.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
    }else{
      _date.setHours(
        _date.getTime() > now.getTime() ? 23 : _date.getTime() < now.getTime() ? 0 : now.getHours(),
        _date.getTime() > now.getTime() ? 59 : _date.getTime() < now.getTime() ? 0 : now.getMinutes(),
        _date.getTime() > now.getTime() ? 59 : _date.getTime() < now.getTime() ? 0 : now.getSeconds()
      );
    }
    switch (_type){
      case 'corta':{
        return formatDate(new Date(_date), 'yyyy-MM-dd', 'en');
      }
      case 'larga':{
        const offset = -_date.getTimezoneOffset();
        const sign = offset >= 0 ? '+' : '-';
        const pad = (num: number): string => String(num).padStart(2, '0');
        const offsetHours = pad(Math.floor(Math.abs(offset) / 60));
        const offsetMinutes = pad(Math.abs(offset) % 60);
        const formattedDate = formatDate(_date, 'yyyy-MM-ddTHH:mm:ss', 'en');
        const timezone = `${sign}${offsetHours}:${offsetMinutes}`;
        return `${formattedDate}${timezone}`;
      }
      default: {
        throw new Error('El valor ingresado no coincide con el esperado.');
      }
    }
  }

  /**
   * Gets the current date adjusted for the local timezone offset.
   * @return The current date with local timezone offset applied.
   */
  DateWithLocalOffset(): Date {
    let date: Date = new Date();
    const tzOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzOffset);
  }
}
